import React, {useState, useEffect} from 'react';
import BusySpinner from '../spinners/BusySpinner';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import NoData from '../page/NoData';
import FilterPanel from './FilterPanel';
import {connect} from 'react-redux';
import SALESANDPURCHASELEDGERS_CONTROLLER from '../../controllers/salesAndLedgers';
import ACCOUNT_SETTINGS_CONTROLLER from '../../controllers/accountSettings';
import Pagination from '../page/Pagination';
import {TableConfig} from '../../utils/appConfig';
import LanguageTranslationPanel from "./LanguageTranslationPanel";
import ExportAllByFileType from "../common/ExportAllByFileType";
import LANGUAGES_CONTROLLER from "../../controllers/languages";
import { FILETYPES_FOR_LEDGER_EXPORT_ALL, FeatureFlagEnum } from "../../utils/enums";
import DOMPurify from 'dompurify';

const SalesAndPurchaseLedger = (props) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [currentPageSize, setCurrentPageSize] = useState(TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE);
    const totalPages = Math.ceil(props.ledger?.totalRecords / currentPageSize);
    const [params, setParams] = useState(null);
    const [fileType, setFileType] = useState(-1);
    const [isFileTypeError, setFileTypeError] = useState(false)
    const [pageSize, setPageSize] = useState(1)

    useEffect(() => {
        if (!props.accountSettings) {
            props.onLoadAccountSettings();
        }
    }, []);

    const onSearchClick = (selection) => {
        const searchParams = {
            companyId: selection.companyId === "All" ? 0 : selection.companyId,
            countryCode: selection.countryCode,
            endDate: selection.endDate,
            startDate: selection.startDate,
            ledgerType: selection.ledgerType,
            pageNumber: 1,
            pageSize: params?.pageSize || TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE,
            translated: false
        }
        setParams(searchParams);
        setCurrentPage(0);
        setFileType(-1);
        props.onApplyFilters(searchParams);
    }

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const pageParams = {
            ...params,
            pageNumber: page + 1,
        }
        setParams(pageParams);
        setFileTypeError(false);
        setFileType(-1);
        props.onApplyFilters(pageParams);
    }

    const handlePageSizeChange = (page) => {
        setCurrentPageSize(page);
        const pageSizeParams = {
            ...params,
            pageSize: page,
        }
        setParams(pageSizeParams);
        setFileTypeError(false);
        setFileType(-1);
        props.onApplyFilters(pageSizeParams);

    }
    const handleLanguageConversionClick= () => {
        const pageSizeParams = {
            ...params,
            translated: !props.isEngTranslated
        }
        setParams(pageSizeParams);
        setFileTypeError(false);
        setFileType(-1);
        props.onApplyFilters(pageSizeParams);
    }
    
    const handleExportAllClick = () => {
        if(Number(fileType) !== -1) {
            const payloadForExport = {
                tenantId: props.customerId,
                companyId: params.companyId,
                countryCode: params.countryCode,
                startDate: params.startDate,
                endDate: params.endDate,
                ledgerType: params.ledgerType,
                translated: params.translated ? 1 : 0,
                fileType: fileType,
                pageSize: pageSize, 
                invoiceFilter: "", // Feature not available on SVR UI, hence sending default
                invertFilter: 0, // Feature not available on SVR UI, hence sending default
                userName: props.userName
            }
            setFileTypeError(false);
            props.onExportAllDatForLedgers(payloadForExport)
        } else {
            setFileTypeError(true)
        }
    }

    const handleToastClose = () => {
        this.props.onResetToastClick();
    }
    
    const onSelectFileType = (option) => {
        const isPdfA4 = option.value === '2';
        setFileType(isPdfA4 ? 1 : Number(option.value));
        // PDF exports share fileType 1, with pageSize distinguishing A3 vs A4.
        setPageSize(isPdfA4 ? 2 : 1);
    }
    
    const getLanguageText = (languages, key) => {
        return props.showToast && languages && LANGUAGES_CONTROLLER.formatUtilFormatter(languages.data, key);
    }

    const getStatusForToast = () => {
        return (props.exportResponse && !props.exportResponse?.isSuccess) ? props.exportResponse?.error.type : 'success';
    }

    const getBannerMessage = () => {
        if (!params || !params.countryCode) {
            return null;
        }

        const currentCountry = params.countryCode;
        const forcedTransactionCountries = ['BE', 'SI', 'IT'];
        
        // For BE, SI, IT countries - always show transaction dates message
        if (forcedTransactionCountries.includes(currentCountry)) {
            return "ledger_banner_transaction_dates";
        }

        // For other countries, check the LedgerBasedUponDate setting
        if (props.accountSettings && props.accountSettings.length > 0) {
            const ledgerSetting = props.accountSettings.find(setting => setting.name === 'LedgerBasedUponDate');
            if (ledgerSetting) {
                // 0 = Invoice Date, 1 = Transaction Date
                return ledgerSetting.value == '0' ? "ledger_banner_invoice_dates" : "ledger_banner_transaction_dates";
            }
        }

        // Default to transaction dates if setting not found
        return "ledger_banner_transaction_dates";
    }
    let featureFlagName = 'ENABLE_EXPORT_ALL_SALES_AND_PURCHASE_LEDGER';
    const enableExportAll = props.featureFlags?.[FeatureFlagEnum.ENABLE_EXPORT_ALL_SALES_AND_PURCHASE_LEDGER];
    const csvOnlyExport = props.featureFlags?.[FeatureFlagEnum.ENABLE_CSV_ONLY_EXPORT];
    let exportOptions = FILETYPES_FOR_LEDGER_EXPORT_ALL;
    if (csvOnlyExport && !enableExportAll) {
        exportOptions = exportOptions.filter(option => option.label === "CSV");
        featureFlagName = 'ENABLE_CSV_ONLY_EXPORT';
    }
    
    return (
        <>
            <BusySpinner busy={false}/>
            <s-container class="mb50" data-testid='sales_container'>
                <div className="content-area">
                    <h1><LanguageStringResolver string="PageName_SalesAndPurchaseLedger"/></h1>
                </div>

                {(() => {
                    const bannerMessage = getBannerMessage();
                    return bannerMessage && (
                        <s-alert class="mb50" status="info" data-testid='ledger-date-banner' nodismiss={true}>
                            <LanguageStringResolver string={bannerMessage}/>
                        </s-alert>
                    );
                })()}

                { isFileTypeError &&
                    <s-alert status="error" data-testid='content-error'>
                        <span> <LanguageStringResolver string="export_ledger_valid_file_type_error"/></span>
                    </s-alert>
                }
                <FilterPanel onSearchClick={onSearchClick} countries={props.countryList}/>
                {props.errors && Array.isArray(props.errors) &&
                    <div data-testid='error-container'>
                        {props.errors.map(error =>
                            <s-alert key={error.field} status="error" data-testid='alert-error'>
                                <span>{error.message}</span>
                            </s-alert>
                        )}
                    </div>
                }
                {props.contentError &&
                    <s-alert status="error" data-testid='content-error'>
                        <span>{props.contentError}</span>
                    </s-alert>
                }
                {props.ledger ?
                    <>
                        <LanguageTranslationPanel isEngTranslated={props.isEngTranslated} translationAvailable={props.translationAvailable} handleClick={handleLanguageConversionClick} />
                        <div className='ledger-container' dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(props.ledger.ledgerData)}}/>
                        <div className='ReactTable' data-testid="ledger-pagination">
                            <Pagination
                                pageSizeOptions={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS}
                                showPageSizeOptions={true}
                                pageSize={currentPageSize}
                                pages={totalPages}
                                page={currentPage}
                                ofText="of"
                                showPageJump={true}
                                onPageChange={handlePageChange}
                                canPrevious={currentPage >= 1}
                                canNext={currentPage < (totalPages - 1)}
                                onPageSizeChange={handlePageSizeChange}/>
                        </div>
                        <ExportAllByFileType
                            isExportEnabled={enableExportAll || csvOnlyExport}
                            exportFor={featureFlagName}
                            label={"global_export_all"}
                            onClose={ () => handleToastClose() }
                            onExport={ () => handleExportAllClick() }
                            onSelect={ e => onSelectFileType(e)}
                            options={exportOptions}
                            showToast={true}
                            elementType='btn'
                            toastType={getStatusForToast()}
                            toasterTitle={getLanguageText(props.languages, 'viewTransmission_ExportAllToastTitle')}
                            toasterDetails={getLanguageText(props.languages, 'viewTransmission_ExportAllToastDetails')}
                        />
                    </>
                    : <NoData noDataText="salesAndPurchase_NoData" {...props} />}

                <s-alert class="mt70" status="warning" data-testid='content-warning'>
                    <div>
                        <LanguageStringResolver string="inAppMessage_Warning_13"/>
                    </div>
                </s-alert>
            </s-container>
        </>
    )
}

export function mapStateToProps(state) {
    const stateProps = {};
    stateProps.countryList = (state.content && state.content.contentData && state.content.contentData.countryList);
    stateProps.ledger = (state.salesAndPurchaseLedger && state.salesAndPurchaseLedger.data);
    stateProps.errors = (state.salesAndPurchaseLedger && state.salesAndPurchaseLedger.error);
    stateProps.isEngTranslated = (state.salesAndPurchaseLedger && state.salesAndPurchaseLedger.data && state.salesAndPurchaseLedger.data.isEngTranslated);
    stateProps.translationAvailable = (state.salesAndPurchaseLedger && state.salesAndPurchaseLedger.data && state.salesAndPurchaseLedger.data.translationAvailable);
    stateProps.contentError = state.salesAndPurchaseLedger?.error?.message;
    stateProps.customerId = state.authentication && state.authentication.data && state.authentication.data.customerId;
    stateProps.userName = (state.authentication && state.authentication.data && state.authentication.data.userName) || "Unknown";
    stateProps.showToast = state.salesAndPurchaseLedger?.showToast;
    stateProps.exportResponse = state.salesAndPurchaseLedger?.exportResponse;
    stateProps.languages = state.languages;
    stateProps.featureFlags = state.featureFlag?.data;
    stateProps.accountSettings = state.accountSettings && state.accountSettings.data;
    return stateProps;
}

function mapDispatchersToProps(dispatch) {
    return {
        onApplyFilters: (params) => {
            dispatch(SALESANDPURCHASELEDGERS_CONTROLLER.doLoadLedgers(params));
        },
        onExportAllDatForLedgers: params => {
            dispatch(SALESANDPURCHASELEDGERS_CONTROLLER.doExportAllDataForLedger(params));
        },
        onLoadAccountSettings: () => {
            dispatch(ACCOUNT_SETTINGS_CONTROLLER.doLoad());
        }
    }
}

export default connect(mapStateToProps, mapDispatchersToProps)(SalesAndPurchaseLedger);